import type { LocationAutocompleteEntryDto } from "@advanced-sales-technologies/backend-client/models/LocationAutocompleteEntryDto";
import type { LocationAutocompleteFilterDto } from "@advanced-sales-technologies/backend-client/models/LocationAutocompleteFilterDto";
import { useQuery } from "@tanstack/react-query";

import useSalesyApiClient from "@/hooks/useSalesyApiClient";

type LocationAutocompleteProps = {
  query: string;
  showTerminated?: boolean;
  resultItems?: Array<"COUNTRY_CODE" | "CITY" | "ZIPCODE" | "STATE" | "REGION">;
  filter?: LocationAutocompleteFilterDto;
};

const locationQueryKey = ({ query, showTerminated, resultItems, filter }: LocationAutocompleteProps) => [
  "location-autocomplete",
  query,
  showTerminated,
  resultItems,
  filter,
];

export const useLocationAutocomplete = ({
  query,
  showTerminated = false,
  filter,
  resultItems,
}: LocationAutocompleteProps) => {
  const { apiClient } = useSalesyApiClient();

  const getLocationAutocomplete = async (): Promise<Array<LocationAutocompleteEntryDto & { id: string }>> => {
    const rawOptions = await apiClient.locations.autocomplete({
      requestBody: {
        query: query ? query : undefined,
        show_non_active: showTerminated,
        filter,
        result_items: resultItems,
      },
    });

    return rawOptions.map((option, index) => ({
      ...option,
      id: `location-${index}-${option.name}`,
    }));
  };

  return useQuery({
    queryKey: locationQueryKey({ query, showTerminated, resultItems, filter }),
    queryFn: getLocationAutocomplete,
    staleTime: 1000 * 60 * 60,
  });
};
